import { useEffect, useRef, useState } from "react";
import { useToast } from "@/hooks/use-toast";
import { jsonStore } from "@/manager/cache/localdb";
import { userAudioTemplateList, VoiceTemplateItemData } from "@/services/user-audio-template-list";
import { AudioTemplatesData } from "@/services/agent-user-get-audio-templates";
import { agentUserActorDetail } from "@/services/agent-user-actor-detail";
import { canUseWindow } from "@/utils/canuseWindow";
import { iamAccountHooks } from "./iam-account-manager";
import { agentUserSaveAudioTemplate } from "@/services/agent-user-save-audio-templates";
import { userAudioTemplateAdd, UserAudioTemplateAddRequest } from "@/services/user-audio-template-add";
import { userAudioTemplateDelete } from "@/services/user-audio-template-delete";

type VoiceItem = VoiceTemplateItemData;

type VoiceListEventParams = {
  templates: VoiceItem[];
};

export interface AgentUserActorData extends Actor {
  createdAt: string;
  generateStatus: number;
}

type VoiceListEvent = CustomEvent<VoiceListEventParams>;

type AgentUserActorsChangeEventDetail = AgentUserActorData[]
type AgentUserActorsChangeEvent = CustomEvent<AgentUserActorsChangeEventDetail>;

type AgentUserVoicesChangeEventDetail = Audio[]
type AgentUserVoicesChangeEvent = CustomEvent<AgentUserVoicesChangeEventDetail>;

class AgentUserVoiceListEvents {
  constructor(private eventName: string) { }
  listen(listener: (e: VoiceListEvent) => void): () => void {
    window.addEventListener(this.eventName, listener as EventListener);
    return () => {
      window.removeEventListener(this.eventName, listener as EventListener);
    };
  }
  dispatch(detail: VoiceListEventParams): void {
    const event = new CustomEvent<VoiceListEventParams>(this.eventName, { detail });
    window.dispatchEvent(event);
  }
}

interface AgentUserVoiceListCacheData {
  templates: VoiceItem[];
  page: number;
  pageSize: number;
  total?: number;
}

class AgentUserVoiceListLocal {
  constructor(private storageKey: string) { }
  async get(): Promise<AgentUserVoiceListCacheData | null> {
    return await jsonStore.get<AgentUserVoiceListCacheData>(this.storageKey);
  }
  async set(data: AgentUserVoiceListCacheData): Promise<void> {
    await jsonStore.set(this.storageKey, data);
  }
  async clear(): Promise<void> {
    await jsonStore.set(this.storageKey, null);
  }
}

class AgentUserVoiceListRemote {
  constructor(
    private cache: AgentUserVoiceListLocal,
    private agentId?: string,
  ) { }
  async getCached() {
    return await this.cache.get();
  }
  async fetchTemplates(page: number, pageSize: number) {
    const res = await userAudioTemplateList({
      agentId: this.agentId || "",
      page,
      pageSize,
    });
    await this.cache.set({
      templates: res.templates,
      page,
      pageSize,
      total: res.total,
    });
    return {
      templates: res.templates,
      page,
      pageSize,
      total: res.total,
    };
  }
  async fetchAgentUserActors(agentId: string) {
    const res = await agentUserActorDetail({
      userId: this.agentId || agentId,
    });
    return res;
  }
  async saveVoiceTemplate(templates: UserAudioTemplateAddRequest[]) {
    const res = await userAudioTemplateAdd(templates);
    return res;
  }
  async removeVoiceTemplate(voiceId: string) {
    if (!this.agentId) return;
    return await userAudioTemplateDelete({
      audioId: voiceId,
      agentId: this.agentId,
    });
  }
}

export class AgentUserVoiceListManager {
  private readonly STORAGE_PREFIX = "MIUTA_AGENT_USER_VOICE_LIST";
  private templates: VoiceItem[] = [];
  private page: number = 1;
  private pageSize: number = 1000;

  private cache: AgentUserVoiceListLocal;
  private api: AgentUserVoiceListRemote;
  private events: AgentUserVoiceListEvents;

  // 用户数字人信息数据（来源资产的数字人信息）
  private agentUserActors: AgentUserActorData[] = [];
  // 用户音频数据（来源资产的音频数据）
  private agentUserVoices: Audio[] = [];

  constructor(
    private agentId?: string,
  ) {
    const key = `${this.STORAGE_PREFIX}:${this.agentId || "all"}:${"all"}`;
    this.cache = new AgentUserVoiceListLocal(key);
    this.api = new AgentUserVoiceListRemote(this.cache, agentId);
    this.events = new AgentUserVoiceListEvents(key);
  }

  public getTemplates(): VoiceItem[] {
    return this.templates;
  }

  private setTemplates(templates: VoiceItem[]) {
    this.templates = templates;
    this.dispatch();
  }

  public getAgentUserActors() {
    return this.agentUserActors
  }
  public setAgentUserActors(actors: AgentUserActorData[]) {
    this.agentUserActors = actors;
    this.dispatchAgentUserActorsChange();
  }

  public getAgentUserVoices() {
    return this.agentUserVoices;
  }
  public setAgentUserVoices(voices: Audio[]) {
    this.agentUserVoices = voices;
    this.dispatchAgentUserVoicesChange();
  }

  public async init() {
    try {
      const cached = await this.api.getCached();
      if (cached) {
        this.setTemplates(cached.templates);
        // this.page = cached.page;
        // this.pageSize = cached.pageSize;
        this.fetch();
        return;
      }
    } catch {
      this.setTemplates([]);
      this.page = 1;
      this.pageSize = 1000;
    }
    await this.fetch();
  }

  public async fetch(page?: number, pageSize?: number) {
    const data = await this.api.fetchTemplates(page ?? this.page, pageSize ?? this.pageSize);
    this.setTemplates(data.templates);
    // this.page = data.page;
    // this.pageSize = data.pageSize;
  }

  public async fetchAgentUserActors(userId: string) {
    if (!userId) return;
    try {
      const data = await agentUserActorDetail({ userId });

      // 处理音频数据
      const voices = data.audios?.map((item) => ({
        id: item.id,
        voiceTtsId: item.voiceTtsId,
        name: item.name,
        modelName: item.provider,
        ageGroup: "",
        language: "",
        gender: "",
        emotion: "",
        speed: 1,
        isStarred: false,
        audioUrl: item.sampleUrl,
        createdAt: item.createdAt,
      }));
      this.setAgentUserVoices(voices);

      // 处理数字人数据
      const actors = data.actors?.map(item => ({
        id: item.id,
        modelId: item.modelId,
        name: item.name,
        video: item.fullActorDefaultVideoUrl,
        tags: item.tags,
        image: item.imageUrl,
        isFavorite: item.isFavorite,
        isMyActor: item.isMyActor,
        createdAt: item.createdAt,
        isPro: item.isPro,
        isHD: item.isHD,
        generateStatus: item.generateStatus,
        generateType: item.generateType,
        width: item.width,
        height: item.height,
        audio: {
          id: item.defaultAudio?.id,
          voiceTtsId: item.defaultAudio?.voiceTtsId,
          modelName: item.defaultAudio?.modelName,
          name: item.defaultAudio?.name,
          speed: item.defaultAudio?.speed,
          audioUrl: item.defaultAudio?.audioUrl,
          isStarred: item.defaultAudio?.isStarred,
          language: '',
          gender: '',
          ageGroup: '',
        },
        isUnusual: item.isUnusual,
      }));

      this.setAgentUserActors(actors);
      // 检查是否有生成中的视频 如果有需要进行单独的任务轮询
      // for (const actor of actors) {
      //   // 状态是生成中且 id 有值才需要轮询
      //   if (actor.generateStatus < 2 && actor.id) {
      //     this.generatorPolling(actor.id);
      //   }
      // }

    } catch (error) {
      this.dispatchAgentUserActorsChange();
      console.error("Failed to fetch agent user actors:", error);
      throw error;
    }
  }

  // 保存用户音频模板
  public async saveVoiceTemplate(templates: { agentId: string; userAudioTemplateId: string; }[]) {
    const data = await this.api.saveVoiceTemplate(templates);
    return data;
  }

  // 删除用户音频模板
  public async removeVoiceTemplate(voiceId: string) {
    const _templates = this.templates;
    try {
      // 前端删除
      this.setTemplates(this.templates.filter(item => item.id !== voiceId));
      // 调接口删除
      const data = await this.api.removeVoiceTemplate(voiceId);
      return data;
    } catch (error) {
      console.error("Failed to remove voice template:", error);
      this.setTemplates(_templates);
      throw error;
    }
  }

  public reset(): void {
    this.setTemplates([]);
    this.page = 1;
    this.pageSize = 1000;
    this.clear();
  }

  public clear(): void {
    this.cache.clear();
  }

  public listen(listener: (e: VoiceListEvent) => void): () => void {
    return this.events.listen(listener);
  }

  private dispatch(): void {
    this.events.dispatch({ templates: this.templates });
  }


  // 监听用户数字人数据变化
  public onAgentUserActorsChange(listener: (e: AgentUserActorsChangeEvent) => void) {
    if (canUseWindow()) {
      const eventName = `${this.STORAGE_PREFIX}:actors-change`;
      window.addEventListener(eventName, listener as EventListener);

      return () => {
        window.removeEventListener(eventName, listener as EventListener);
      };
    }
    return () => { };
  }
  public dispatchAgentUserActorsChange() {
    if (canUseWindow()) {
      const event = new CustomEvent<AgentUserActorsChangeEventDetail>(
        `${this.STORAGE_PREFIX}:actors-change`, {
        detail: this.agentUserActors,
      });
      window.dispatchEvent(event);
    }
  }

  // 监听用户音频数据变化
  public onAgentUserVoicesChange(listener: (e: AgentUserVoicesChangeEvent) => void) {
    if (canUseWindow()) {
      const eventName = `${this.STORAGE_PREFIX}:voices-change`;
      window.addEventListener(eventName, listener as EventListener);

      return () => {
        window.removeEventListener(eventName, listener as EventListener);
      };
    }
    return () => { };
  }
  public dispatchAgentUserVoicesChange() {
    if (canUseWindow()) {
      const event = new CustomEvent<AgentUserVoicesChangeEventDetail>(
        `${this.STORAGE_PREFIX}:voices-change`, {
        detail: this.agentUserVoices,
      });
      window.dispatchEvent(event);
    }
  }

}

export const agentUserVoiceListHooks = {
  useVoiceListData(
    props: {
      agentId?: string;
      type?: "video" | "podcast" | "moments" | "post" | "article" | "";
      page?: number;
      pageSize?: number;
    }
  ) {
    const { agentId, page = 1, pageSize = 1000 } = props;
    const managerRef = useRef<AgentUserVoiceListManager | null>(null);
    const [templates, setTemplates] = useState<VoiceItem[]>([]);
    const [loading, setLoading] = useState(false);
    const [saveLoading, setSaveLoading] = useState(false);
    const { showToastByCode } = useToast();

    useEffect(() => {
      managerRef.current = new AgentUserVoiceListManager(agentId);
      setTemplates(managerRef.current.getTemplates());
      const unsubscribe = managerRef.current.listen((e) => setTemplates(e.detail.templates));
      return unsubscribe;
    }, [agentId]);

    useEffect(() => {
      const init = async () => {
        if (!managerRef.current) return;
        setLoading(true);
        try {
          await managerRef.current.init();
        } catch (error) {
          showToastByCode(error);
        } finally {
          setLoading(false);
        }
      };
      init();
    }, [agentId, page, pageSize]);

    const refresh = async (nextPage?: number, nextPageSize?: number) => {
      if (!managerRef.current) return;
      setLoading(true);
      try {
        await managerRef.current.fetch(nextPage ?? page, nextPageSize ?? pageSize);
      } catch (error) {
        showToastByCode(error);
      } finally {
        setLoading(false);
      }
    };

    // 保存用户音频模板
    const saveVoiceTemplate = async (voiceIds: string[]) => {
      if (!managerRef.current) return;
      setSaveLoading(true);
      try {
        await managerRef.current.saveVoiceTemplate(voiceIds.map(id => ({
          agentId,
          userAudioTemplateId: id
        })));
        refresh();
      } catch (error) {
        showToastByCode(error);
      } finally {
        setSaveLoading(false);
      }
    };

    // 删除用户音频模板
    const removeVoiceTemplate = async (voiceId: string) => {
      if (!managerRef.current) return;
      try {
        await managerRef.current.removeVoiceTemplate(voiceId);
      } catch (error) {
        showToastByCode(error);
      }
    };

    return {
      templates,
      loading,
      refresh,
      saveVoiceTemplate,
      removeVoiceTemplate,
      saveLoading,
    };
  },
  useUserActorAndVoiceData() {
    const { showToastByCode } = useToast();
    const [loading, setLoading] = useState(false);
    const managerRef = useRef<AgentUserVoiceListManager | null>(null);
    const { account } = iamAccountHooks.useData();

    const [agentUserActors, setAgentUserActors] = useState<AgentUserActorData[]>([]);
    const [agentUserVoices, setAgentUserVoices] = useState<Audio[]>([]);

    useEffect(() => {
      managerRef.current = new AgentUserVoiceListManager();
      setAgentUserActors(managerRef.current.getAgentUserActors());
      const unsubscribe = managerRef.current.onAgentUserActorsChange((e) => setAgentUserActors(e.detail));
      return unsubscribe;
    }, []);

    useEffect(() => {
      setAgentUserVoices(managerRef.current.getAgentUserVoices());
      const unsubscribe = managerRef.current.onAgentUserVoicesChange((e) => setAgentUserVoices(e.detail));
      return unsubscribe;
    }, []);


    const init = async () => {
      if (!managerRef.current) return;
      setLoading(true);
      try {
        await managerRef.current.fetchAgentUserActors(account?.id);
      } catch (error) {
        showToastByCode(error);
      } finally {
        setLoading(false);
      }
    }

    useEffect(() => {
      init();
    }, []);

    return {
      loading,
      agentUserActors,
      agentUserVoices,
    }
  }
};

export default AgentUserVoiceListManager;
